import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import {
  Settings,
  Logout as LogoutIcon,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { isDriverPortalOnlyRole } from '../constants/roles';
import { API_BASE_URL } from '../api';
import { useTheme } from '@mui/material/styles';
import DriverPortalSchedule from '../components/DriverPortalSchedule';
import { subscribeDriverWebPush } from '../utils/webPush';
import { loadDriverJobsCache, saveDriverJobsCache } from '../utils/driverPortalJobCache';

const DriverPortal = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [userResolved, setUserResolved] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsOffline, setJobsOffline] = useState(false);
  const [jobsCacheSavedAt, setJobsCacheSavedAt] = useState('');
  const [uploadError, setUploadError] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [statusChangeSuccess, setStatusChangeSuccess] = useState(false);
  const [statusChangeError, setStatusChangeError] = useState(false);
  const [jobLocations, setJobLocations] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [pushLoading, setPushLoading] = useState(false);
  const [pushHint, setPushHint] = useState('');
  const theme = useTheme();

  const selectedCalendarDay = React.useMemo(() => {
    const raw = searchParams.get('day');
    if (!raw) return null;
    const d = dayjs(raw, 'YYYY-MM-DD', true);
    return d.isValid() ? d : null;
  }, [searchParams]);

  const goToCalendarView = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('day');
    setSearchParams(next, { replace: true });
  };

  const goToDayView = (day) => {
    setSearchParams({ day: day.format('YYYY-MM-DD') }, { replace: false });
  };

  // Function to geocode address and get coordinates
  const geocodeAddress = async (address) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Fetch user data from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
    setUserResolved(true);
  }, []);

  // When the driver opens the portal, clear any app icon badge (if supported).
  useEffect(() => {
    if (!userResolved || !user) return;
    try {
      if (navigator?.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'WORKWISE_CLEAR_BADGE' });
      }
    } catch {
      /* ignore */
    }
  }, [userResolved, user]);

  // Jobs from /driver-portal/jobs; localStorage cache when offline.
  useEffect(() => {
    if (!userResolved || !user) return;

    const userId = user.id;

    const hydrateGeocodes = (list) => {
      list.forEach((job) => {
        if (job.address && !jobLocations[job.id]) {
          geocodeAddress(job.address).then((coords) => {
            if (coords) {
              setJobLocations((prev) => ({
                ...prev,
                [job.id]: coords,
              }));
            }
          });
        }
      });
    };

    const cached = loadDriverJobsCache(userId);
    if (cached?.jobs?.length) {
      setJobs(cached.jobs);
      setJobsCacheSavedAt(cached.savedAt || '');
      hydrateGeocodes(cached.jobs);
    }

    const fetchJobs = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setJobsLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}driver-portal/jobs`, {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data?.data) ? data.data : [];

          setJobs(list);
          setJobsOffline(false);
          setJobsCacheSavedAt(data?.meta?.fetched_at || new Date().toISOString());
          saveDriverJobsCache(userId, list);
          hydrateGeocodes(list);
        } else if (cached?.jobs?.length) {
          setJobsOffline(true);
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
        if (cached?.jobs?.length) {
          setJobsOffline(true);
        }
      } finally {
        setJobsLoading(false);
      }
    };

    fetchJobs();

    const interval = setInterval(fetchJobs, 30000);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchJobs();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userResolved, user]);

  // Handle file upload
  const handleFileUpload = async (jobId, files) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const formData = new FormData();
      
      for (let i = 0; i < files.length; i++) {
        formData.append('files[]', files[i]);
      }

      const response = await fetch(`${API_BASE_URL}collections/${jobId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setUploadSuccess(true);
        // Refresh jobs to get updated file list
        const updatedJobs = jobs.map(job => 
          job.id === jobId 
            ? { ...job, files: [...(job.files || []), ...Array.from(files).map(f => ({ name: f.name }))] }
            : job
        );
        setJobs(updatedJobs);
      } else {
        setUploadError(true);
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadError(true);
    }
  };

  // Handle status change
  const handleStatusChange = async (jobId, newStatus, driverNotes = null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const requestBody = { status: newStatus };
      if (driverNotes !== null) {
        requestBody.driver_notes = driverNotes;
      }

      const response = await fetch(`${API_BASE_URL}collections/${jobId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        setStatusChangeSuccess(true);
        // Update job status in local state
        const updatedJobs = jobs.map(job => 
          job.id === jobId 
            ? { ...job, status: newStatus, driver_notes: driverNotes }
            : job
        );
        setJobs(updatedJobs);
      } else {
        setStatusChangeError(true);
      }
    } catch (error) {
      console.error('Error changing status:', error);
      setStatusChangeError(true);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPasswordError('Authentication required');
        return;
      }

      const response = await fetch(`${API_BASE_URL}users/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
          new_password_confirmation: passwordData.confirm_password
        })
      });

      if (response.ok) {
        setPasswordSuccess(true);
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        setTimeout(() => setPasswordSuccess(false), 3000);
      } else {
        const data = await response.json();
        setPasswordError(data.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError('Network error. Please try again.');
    }
  };

  // Handle driver notes update
  const handleDriverNotesUpdate = async (jobId, driverNotes) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}collections/${jobId}/driver-notes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ driver_notes: driverNotes }),
      });

      if (response.ok) {
        setStatusChangeSuccess(true);
        // Update job notes in local state
        const updatedJobs = jobs.map(job => 
          job.id === jobId 
            ? { ...job, driver_notes: driverNotes }
            : job
        );
        setJobs(updatedJobs);
      } else {
        setStatusChangeError(true);
      }
    } catch (error) {
      console.error('Error updating driver notes:', error);
      setStatusChangeError(true);
    }
  };

  // Handle job completion
  const handleCompleteJob = async (jobId) => {
    await handleStatusChange(jobId, 'completed');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate(user && isDriverPortalOnlyRole(user.role) ? '/drivers' : '/login');
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    setPushHint('');
    try {
      const result = await subscribeDriverWebPush();
      if (result.ok) {
        setPushHint('Notifications are on. You will get a message when a new task is assigned (if the browser allows it).');
      } else if (result.reason === 'denied') {
        setPushHint('Notification permission was denied. You can enable it in your browser settings.');
      } else if (result.reason === 'unsupported') {
        setPushHint('This browser does not support push notifications. Try Safari on iPhone (Add to Home Screen) or Chrome.');
      } else if (result.reason === 'no_vapid') {
        setPushHint('The server has no VAPID keys — an admin should set VAPID_PUBLIC_KEY in .env.');
      } else if (result.reason === 'save_failed') {
        const extra = result.status ? ` (HTTP ${result.status})` : '';
        const detail = result.body ? `\n\n${result.body}` : '';
        setPushHint(`Could not save the subscription${extra}.${detail}`);
      } else if (result.reason === 'exception') {
        setPushHint(`Push setup failed: ${result.message || 'unknown error'}`);
      } else {
        setPushHint('Could not save the subscription. Please try again.');
      }
    } finally {
      setPushLoading(false);
    }
  };

  if (!userResolved) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/drivers" replace />;
  }

  if (jobsLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center', fontWeight: 500, fontSize: '1rem' }}>
          Loading your tasks...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%',
      padding: { xs: 2, sm: 3, md: 4 },
      backgroundColor: 'background.default',
      minHeight: '100vh'
    }}>
      {selectedCalendarDay ? (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            position: 'sticky',
            top: { xs: 0, sm: 0 },
            zIndex: 11,
            py: 1,
            bgcolor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={goToCalendarView}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          >
            Calendar
          </Button>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ alignSelf: 'center', flex: '1 1 auto', minWidth: 120 }}
          >
            {selectedCalendarDay.format('dddd, D MMMM YYYY')}
          </Typography>
        </Box>
      ) : null}

      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 3,
        gap: { xs: 2, sm: 0 }
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 700, 
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            Welcome, {user.name || 'Driver'}!
          </Typography>
          {user.role === 'driver_pro' && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, textAlign: { xs: 'center', sm: 'left' } }}
            >
              You see your tasks and your trainee’s tasks under your supervision.
            </Typography>
          )}

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              disabled={pushLoading}
              onClick={handleEnablePush}
              sx={{ fontWeight: 600 }}
            >
              {pushLoading ? '…' : 'Enable notifications for new tasks'}
            </Button>
            {typeof Notification !== 'undefined' && Notification.permission === 'granted' && (
              <Chip size="small" label="Notifications: allowed" color="success" variant="outlined" />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', maxWidth: 560 }}>
            For notifications on iPhone: iOS 16.4+, open this site in Safari, use &quot;Add to Home Screen&quot;,
            then enable notifications. HTTPS is required in production.
          </Typography>
          {pushHint && (
            <Alert severity="info" sx={{ mt: 2, width: '100%' }}>
              {pushHint}
            </Alert>
          )}
        </Box>

        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          alignItems: 'center',
          justifyContent: { xs: 'center', sm: 'flex-end' }
        }}>
          <Button
            variant="outlined"
            onClick={() => setShowSettings(true)}
            startIcon={<Settings />}
            sx={{
              fontWeight: 600,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Settings
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              fontWeight: 600,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Log out
          </Button>
        </Box>
      </Box>

      {jobsOffline ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Offline mode — showing your last saved job list
          {jobsCacheSavedAt ? ` (saved ${new Date(jobsCacheSavedAt).toLocaleString()})` : ''}.
          Connect to refresh.
        </Alert>
      ) : null}

      {jobsLoading && jobs.length === 0 ? <LinearProgress sx={{ mb: 2 }} /> : null}

      <DriverPortalSchedule
        jobs={jobs}
        selectedDay={selectedCalendarDay}
        onSelectedDayChange={(day) => {
          if (!day) goToCalendarView();
          else goToDayView(day);
        }}
        onFileUpload={handleFileUpload}
        onStatusChange={handleStatusChange}
        onCompleteJob={handleCompleteJob}
      />

      {/* Settings Dialog */}
      <Dialog 
        open={showSettings} 
        onClose={() => setShowSettings(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={window.innerWidth < 600}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: { xs: 0, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: theme.palette.text.primary, 
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          Driver Settings
        </DialogTitle>
        <form onSubmit={handlePasswordChange}>
          <DialogContent sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: { xs: 1.5, sm: 2 },
            px: { xs: 2, sm: 3 }
          }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              fontSize: { xs: '0.875rem', sm: '1rem' }, 
              mb: 1,
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              Change Password
            </Typography>
            
            {passwordError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            )}
            
            {passwordSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Password changed successfully!
              </Alert>
            )}
            
            <TextField
              name="current_password"
              label="Current Password"
              type="password"
              value={passwordData.current_password}
              onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
              required
              fullWidth
              size="small"
            />
            
            <TextField
              name="new_password"
              label="New Password"
              type="password"
              value={passwordData.new_password}
              onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
              required
              fullWidth
              size="small"
            />
            
            <TextField
              name="confirm_password"
              label="Confirm New Password"
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
              required
              fullWidth
              size="small"
            />
          </DialogContent>
          <DialogActions sx={{ 
            p: { xs: 2, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 }
          }}>
            <Button 
              onClick={() => setShowSettings(false)} 
              sx={{ 
                color: theme.palette.text.secondary, 
                fontWeight: 500,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ 
                fontWeight: 600,
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Change Password
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbars for feedback */}
      <Snackbar open={uploadSuccess} autoHideDuration={3000} onClose={() => setUploadSuccess(false)}>
        <Alert severity="success">Files uploaded successfully!</Alert>
      </Snackbar>
      
      <Snackbar open={uploadError} autoHideDuration={3000} onClose={() => setUploadError(false)}>
        <Alert severity="error">Error uploading files!</Alert>
      </Snackbar>
      
      <Snackbar open={statusChangeSuccess} autoHideDuration={3000} onClose={() => setStatusChangeSuccess(false)}>
        <Alert severity="success">Status updated successfully!</Alert>
      </Snackbar>
      
      <Snackbar open={statusChangeError} autoHideDuration={3000} onClose={() => setStatusChangeError(false)}>
        <Alert severity="error">Error updating status!</Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverPortal; 